import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Map, View, Feature, Overlay } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import { Vector, OSM } from 'ol/source';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import PointGeom from 'ol/geom/Point';
import * as olProj from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { Subscription } from 'rxjs';
import { Mission } from 'src/app/storage/models/mission.model';
import { CONSTANTS } from 'src/app/constants';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MarketService } from 'src/app/providers/services/market.service';
import Geometry from 'ol/geom/Geometry';
import { StorageService } from 'src/app/providers/services/storage.service';
import { Zoom } from 'ol/control';
import { Market } from 'src/app/storage/models/market.model';

const { CONFIG_STATUS } = CONSTANTS.MISSION

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MapComponent implements OnInit, OnDestroy {
  missions: Mission[] = [];
  displayedMissions: Mission[] = [];
  selectedMissions: Mission[] = [];

  selectedMarket: Market;
  selectedMarketSubscription: Subscription;

  statusList = [
    CONFIG_STATUS.NOT_STARTED,
    CONFIG_STATUS.IN_PROGRESS,
    CONFIG_STATUS.COMPLETED,
    CONFIG_STATUS.BLOCKED
  ];
  selectedStatus: number[] = [
    CONFIG_STATUS.NOT_STARTED,
    CONFIG_STATUS.IN_PROGRESS,
    CONFIG_STATUS.COMPLETED
  ];

  private map: Map;
  private vector: Vector<Geometry> = new Vector({ features: [] });
  private overlay: Overlay;
  private missionsSubscription: Subscription;
  
  statusTranslations: any;

  constructor(
    private router: Router,
    private missionsService: MissionsService,
    private translateService: TranslateService,
    private marketService: MarketService,
    private storageService: StorageService
  ) { }

  async ngOnInit(): Promise<void> {
    this.statusTranslations = await this.translateService.get('STATUS').toPromise();

    await this.missionsService.getMissions();
    this.generateMap();

    // Set stored status filters
    const statusFilter = this.storageService.getData(CONSTANTS.STORAGE.STATUS_FILTERS);
    if (statusFilter) { this.selectedStatus = JSON.parse(statusFilter); }

    this.missionsSubscription = this.missionsService.missions$.subscribe((missions: Mission[]) => {
      this.missions = missions;
      this.displayedMissions = missions;
      this.displayMissions();
    });

    this.selectedMarketSubscription = this.marketService.selectedMarket$.subscribe(selectedMarket => {
      if (selectedMarket) {
        this.selectedMarket = selectedMarket;
        this.displayMissions();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.missionsSubscription) { this.missionsSubscription.unsubscribe(); }
    if (this.selectedMarketSubscription) { this.selectedMarketSubscription.unsubscribe(); }
  }

  getStatusColor(status: number): string {
    const STATUS = CONSTANTS.MISSION.CONFIG_STATUS;
    switch (status) {
      case STATUS.IN_PROGRESS:
        return "#334694";
      case STATUS.COMPLETED:
        return "#38B346";
      case STATUS.BLOCKED:
        return "#f39900";
      case STATUS.NOT_STARTED:
      default:
        return "#838281";
    }
  }

  isStatusSelected(status: number): boolean {
    return this.selectedStatus.find(st => st === status) != null;
  }

  toggleStatusSelection(status: number): void {
    this.selectedStatus = this.selectedStatus.find(st => st === status) == null ?
                          [...this.selectedStatus, status] :
                          this.selectedStatus.filter(st => st !== status);

    // Store status filters
    this.storageService.setData(CONSTANTS.STORAGE.STATUS_FILTERS, JSON.stringify(this.selectedStatus));
    this.displayMissions();
  }

  displayMissions(): void {
    this.displayedMissions = this.missions
                            // Filter on selected status & market
                            .filter(mission => this.selectedStatus.find(status => status === mission.status) != null)
                            .filter(mission => this.selectedMarket ? (mission.marketname === this.selectedMarket.marketname) : false);

    this.addMapMarkers();
  }

  async selectMission(mission: Mission): Promise<void> {
    const route = `${CONSTANTS.ROUTES.HOME}/${CONSTANTS.ROUTES.MISSION_CONFIG}`;
    this.missionsService.selectedMission$.next(mission);
    await this.router.navigateByUrl(route);
  }

  private generateMap(): void {
    // Elements that make up the popup.
    const container = document.getElementById('popup');
    const closer = document.getElementById('popup-closer');

    // Build popup overlay
    const overlay = new Overlay({
      element: container,
      autoPan: true,
      autoPanAnimation: {
        duration: 250,
      },
    });

    // Add a click handler to hide the popup.
    closer.onclick = function () {
      overlay.setPosition(undefined);
      closer.blur();
      return false;
    };

    // Generate map
    const view = new View({ center: [0, 0], zoom: 0 });
    view.centerOn([0, 50], [0, 50], [0, 50]);

    this.map = new Map({
      target: 'map',
      overlays: [overlay],
      layers: [
        new TileLayer({ source: new OSM() })
      ],
      view: view,
      controls: [new Zoom()]
    });

    // Listen to click events on features
    this.map.on('click', (evt) => {
      // Find features on position
      const features = [];
      this.map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        features.push(feature);
      });

      // Add overlay
      if (features.length > 0) {
        const missions: Mission[] = features.reduce((acc, cur) => {
          return [...acc, ...cur.getProperties().missions]
        }, []);
        const coordinates = features[0].getGeometry().getCoordinates();

        overlay.setPosition(coordinates);
        this.selectedMissions = missions;
      }
    });

    // Add vector layer for markers
    const vectorLayer = new VectorLayer({
      source: this.vector
    });

    this.map.addLayer(vectorLayer);

    this.overlay = overlay;
  }

  private addMapMarkers(): void {
    this.removeMapMarkers();

    // Group missions by location
    const missionsByLocation: Mission[][] = 
      this.displayedMissions
      .reduce((acc, mission, index, missions) => {
        const coordinates = mission.getCoordinates();
        const missionsWithCoordinates: Mission[] = missions.filter(_mission => _mission.hasCoordinates(coordinates));

        if (acc.find(_missionsWithCoordinates => _missionsWithCoordinates.find((_mission: Mission) => _mission.missionId === mission.missionId))) {
          return acc;
        } else {
          return [...acc, missionsWithCoordinates];
        }
      }, []);

    // Set a marker for each location
    missionsByLocation.map((missions: Mission[]) => {
      // Set marker style
      const missionsMinStatus = Math.min(...missions.map(mission => mission.status));

      const style = new Style({
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: "fraction",
          anchorYUnits: "fraction",
          src: "assets/images/location.svg",
          color: this.getStatusColor(missionsMinStatus)
        })
      });

      if (missions.length > 1) {
        style.setText(new Text({
          text: missions.length.toString(),
          scale: 1.2,
          fill: new Fill({
            color: "#fff"
          }),
          stroke: new Stroke({
            color: "red",
            width: 3
          })
        }));
      }

      // Set marker
      const [longitude, latitude] = missions[0].getCoordinates();
      const feature = new Feature({
        geometry: new PointGeom(olProj.transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857')),
        missions: missions
      });
    
      feature.setStyle(style);

      // Add marker to map
      this.vector.addFeature(feature);
    });
  }

  private removeMapMarkers(): void {
    this.overlay.setPosition(undefined);
    const features = this.vector.getFeatures();

    features.map((feature) => {
      this.vector.removeFeature(feature);
    });
  }
}
